import { useState, useEffect } from 'react';
import { getLLMConfig, saveLLMConfig } from '../../utils/accountStorage';

const FUNCTION_KEYS = [
  { key: 'scanReceipt',       label: 'Receipt Scanning',              defaultModel: 'gemini-flash-lite-latest' },
  { key: 'detectCSVFormat',   label: 'CSV Format Detection',          defaultModel: 'gemini-flash-lite-latest' },
  { key: 'reconcile',         label: 'Reconciliation',                defaultModel: 'gemini-flash-lite-latest' },
  { key: 'bookkeeper',        label: 'AI Bookkeeper (main)',          defaultModel: 'gemini-flash-lite-latest' },
  { key: 'bookkeeperSummary', label: 'AI Bookkeeper (summarization)', defaultModel: 'gemini-flash-lite-latest' },
];


const BLANK_MODEL_ENTRY = { id: '', displayName: '', description: '', factor: 1.0, enabled: true, minTier: null };
const TIER_OPTIONS = [
  { value: '',        label: 'Any tier' },
  { value: 'starter', label: 'Standard+' },
  { value: 'pro',     label: 'Pro only' },
];

function AdminLLMEditor() {
  const [config,      setConfig]      = useState(null);
  const [userModels,  setUserModels]  = useState([]);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState(null);

  useEffect(() => {
    getLLMConfig().then(cfg => {
      setConfig(cfg);
      setUserModels(cfg?.userModels ?? []);
    });
  }, []);

  const setField = (key, field, value) =>
    setConfig(prev => ({ ...prev, [`${key}${field}`]: value }));

  const setModelEntry = (idx, field, value) =>
    setUserModels(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Strip empty strings so unset fields revert to code defaults;
      // always include userModels array (even if empty) to avoid dropping it on save
      const toSave = {
        ...Object.fromEntries(
          Object.entries(config).filter(([k, v]) => k !== 'userModels' && v !== '' && v !== null && v !== undefined)
        ),
        userModels,
      };
      await saveLLMConfig(toSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError('Save failed — ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 13 }}>Loading…</div>;
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 20, lineHeight: 1.5 }}>
        Override the model and cost factor for each function. Leave blank to use the code default.
        Factor is a cost multiplier applied to token counts for per-user limit enforcement and admin usage tracking.
      </div>

      {FUNCTION_KEYS.map(({ key, label, defaultModel }) => {
        const model         = config[`${key}Model`]         ?? '';
        const factor        = config[`${key}Factor`]        ?? '';
        const thinkingBudget = config[`${key}ThinkingBudget`] ?? '';
        const thinkingLevel  = config[`${key}ThinkingLevel`]  ?? '';
        return (
          <div key={key} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
              {label}
            </div>
            {/* Row 1: model + factor */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={model}
                  onChange={e => setField(key, 'Model', e.target.value)}
                  placeholder={defaultModel}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 7,
                    fontSize: 12, fontFamily: 'monospace', outline: 'none', color: '#374151',
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>factor</span>
                <input
                  type="number"
                  value={factor}
                  onChange={e => setField(key, 'Factor', e.target.value)}
                  placeholder="1.0"
                  min="0.01"
                  step="0.1"
                  style={{
                    width: 70, padding: '8px 8px', border: '1.5px solid #E5E7EB', borderRadius: 7,
                    fontSize: 12, textAlign: 'right', outline: 'none', fontFamily: 'inherit',
                  }}
                />
              </div>
            </div>
            {/* Row 2: thinking budget + thinking level */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                  thinking budget
                </span>
                <input
                  type="number"
                  value={thinkingBudget}
                  onChange={e => setField(key, 'ThinkingBudget', e.target.value)}
                  placeholder="off"
                  min="-1"
                  step="256"
                  title="Gemini 2.5 only. Flash: 0–24576 (0=off, -1=dynamic). Pro: 128–32768 (-1=dynamic, cannot disable)."
                  style={{
                    width: 80, padding: '7px 8px', border: '1.5px solid #E5E7EB', borderRadius: 7,
                    fontSize: 12, textAlign: 'right', outline: 'none', fontFamily: 'inherit',
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                  thinking level
                </span>
                <select
                  value={thinkingLevel}
                  onChange={e => setField(key, 'ThinkingLevel', e.target.value)}
                  title="Gemini 3.x and Gemma 4. Budget takes precedence if both are set."
                  style={{
                    padding: '7px 8px', border: '1.5px solid #E5E7EB', borderRadius: 7,
                    fontSize: 12, outline: 'none', fontFamily: 'inherit', color: thinkingLevel ? '#374151' : '#9CA3AF',
                    background: '#fff',
                  }}
                >
                  <option value="">off</option>
                  <option value="none">none</option>
                  <option value="minimal">minimal</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                </select>
              </div>
            </div>
          </div>
        );
      })}

      {/* User-selectable models */}
      <div style={{ marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
          User-selectable models
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12, lineHeight: 1.5 }}>
          Users see this list when they type <code style={{ fontFamily: 'monospace', background: '#F3F4F6', padding: '1px 4px', borderRadius: 3 }}>/model</code> in the AI chat. First entry is the default.
        </div>

        {userModels.map((entry, idx) => (
          <div key={idx} style={{ marginBottom: 12, padding: '10px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, background: '#FAFAFA' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <input
                type="text"
                value={entry.id}
                onChange={e => setModelEntry(idx, 'id', e.target.value)}
                placeholder="gemini-flash-lite-latest"
                style={{ flex: 2, padding: '7px 9px', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', outline: 'none' }}
              />
              <input
                type="text"
                value={entry.displayName}
                onChange={e => setModelEntry(idx, 'displayName', e.target.value)}
                placeholder="Display name"
                style={{ flex: 2, padding: '7px 9px', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 12, outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <input
                type="text"
                value={entry.description}
                onChange={e => setModelEntry(idx, 'description', e.target.value)}
                placeholder="Short description shown to users"
                style={{ flex: 1, padding: '7px 9px', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 12, outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>factor</span>
                <input
                  type="number"
                  value={entry.factor ?? 1.0}
                  onChange={e => setModelEntry(idx, 'factor', parseFloat(e.target.value) || 1.0)}
                  min="0.01" step="0.1"
                  style={{ width: 64, padding: '6px 7px', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 12, textAlign: 'right', outline: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>min tier</span>
                <select
                  value={entry.minTier ?? ''}
                  onChange={e => setModelEntry(idx, 'minTier', e.target.value || null)}
                  style={{ padding: '6px 8px', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 12, outline: 'none', background: '#fff' }}
                >
                  {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={entry.enabled !== false}
                  onChange={e => setModelEntry(idx, 'enabled', e.target.checked)}
                />
                Enabled
              </label>
              <button
                onClick={() => setUserModels(prev => prev.filter((_, i) => i !== idx))}
                style={{ marginLeft: 'auto', padding: '5px 10px', border: '1.5px solid #FCA5A5', borderRadius: 6, background: '#FEF2F2', color: '#DC2626', fontSize: 11, cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={() => setUserModels(prev => [...prev, { ...BLANK_MODEL_ENTRY }])}
          style={{ padding: '8px 14px', border: '1.5px dashed #D1D5DB', borderRadius: 7, background: '#fff', color: '#6B7280', fontSize: 12, cursor: 'pointer', width: '100%', marginBottom: 4 }}
        >
          + Add model
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 4, padding: '10px 12px', background: '#FEF2F2', borderRadius: 8, fontSize: 12, color: '#DC2626' }}>
          {error}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          marginTop: 8, width: '100%', padding: '13px', borderRadius: 10,
          border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
          background: saved ? '#10B981' : 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)',
          color: '#fff', fontWeight: 700, fontSize: 14, transition: 'background 0.2s',
        }}
      >
        {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Model Config'}
      </button>

      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, textAlign: 'center' }}>
        Changes take effect within 5 minutes (server-side cache TTL).
      </div>
    </div>
  );
}

export default AdminLLMEditor;
